import type { MapDef } from '../world/map';
import { T } from '../world/tiles';
import { fbm, hash2, hex, mix, noise2, scale, smoothstep, type RGB } from './noise';

/**
 * Ground materials of the tainan package. Tile chars reserved for it: '&', '*' walkable, '^' solid
 * (solidity is declared in src/world/tiles.ts). Runs inside the ground worker, so keep it pure:
 * no DOM, no game state. Return null for chars you don't own.
 *
 *  '&'  old grey stone slabs with mossy joints (temple courtyards, quays); inside the HSR plaza they
 *       are big, pale modern granite tiles with a dark guide strip; inside the fort, warm coral-lime
 *       slabs (Anping's oyster-shell mortar)
 *  '*'  old faded-brick herringbone lanes (tan, the odd red or grey brick), worn pale in the middle; greasy and grey in the market
 *  '^'  walls: inside the fort, Dutch red brick with a lime crust and moss; elsewhere the Confucius Temple's
 *       vermilion wall — a tile-coped top row, and a painted south face where the row above is also '^'
 */

type TRect = readonly [number, number, number, number];
const inRect = (tx: number, ty: number, r: TRect) => tx >= r[0] && ty >= r[1] && tx < r[0] + r[2] && ty < r[1] + r[3];
const at = (map: MapDef, tx: number, ty: number) => (tx < 0 || ty < 0 || tx >= map.w || ty >= map.h ? ' ' : map.rows[ty][tx]);

/** Tile rects on the tainan map (see maps/tainan.ts). */
const HSR_PLAZA: TRect = [74, 1, 18, 21];
const FORT: TRect = [2, 1, 16, 12];
const MARKET_LANE: TRect = [34, 31, 17, 2];

const K = {
  slabA: hex('#b9b6aa'),
  slabB: hex('#9c9a90'),
  slabJoint: hex('#6d6b62'),
  moss: hex('#6f8a4a'),
  graniteA: hex('#d9d6cf'),
  graniteB: hex('#c3c0b8'),
  graniteJoint: hex('#9a978f'),
  guide: hex('#5b5a58'),
  coralA: hex('#e0cfae'),
  coralB: hex('#c9b48c'),
  coralJoint: hex('#9a8664'),
  brickA: hex('#b8896a'),
  brickB: hex('#cda086'),
  brickC: hex('#a0715a'),
  brickRed: hex('#b35a44'),
  brickPale: hex('#dcc6b0'),
  mortar: hex('#e4d9c9'),
  wallRed: hex('#b8382c'),
  wallRedL: hex('#cf4a3a'),
  coping: hex('#4a4e55'),
  copingL: hex('#6d737c'),
  dutchA: hex('#9c4a33'),
  dutchB: hex('#b85f44'),
  lime: hex('#e8dcc4'),
};

/** Old stone slabs in rows of varying width, moss creeping into the joints. */
function slabs(x: number, y: number, seed: number): RGB {
  const rowH = 7;
  const row = Math.floor(y / rowH);
  const off = hash2(row, 0, seed) * 10;
  const w = 10 + Math.floor(hash2(row, 1, seed) * 3);
  const col = Math.floor((x + off) / w);
  const fx = x + off - col * w, fy = y - row * rowH;
  const tone = hash2(col, row, seed + 1);
  let c = mix(K.slabA, K.slabB, tone * 0.75 + noise2(x * 0.35, y * 0.35, seed + 2) * 0.25);
  c = mix(c, hex('#8c9a8a'), fbm(x / 24, y / 24, seed + 3) * 0.25);
  const joint = fx < 0.8 || fy < 0.8;
  if (joint) {
    c = mix(c, K.slabJoint, 0.7);
    if (hash2(Math.floor(x / 2), Math.floor(y / 2), seed + 4) < 0.35) c = mix(c, K.moss, 0.55);
  } else if (fy < 1.6) c = scale(c, 1.06);
  if (hash2(Math.floor(x), Math.floor(y), seed + 5) < 0.04) c = scale(c, 0.9);
  return c;
}

/** Modern granite: big pale tiles, a dark tactile/guide strip every 6 tiles. */
function granite(map: MapDef, x: number, y: number): RGB {
  const s = 12;
  const col = Math.floor(x / s), row = Math.floor(y / s);
  const fx = x - col * s, fy = y - row * s;
  const tone = hash2(col, row, 611);
  let c = mix(K.graniteA, K.graniteB, tone * 0.6 + noise2(x * 0.5, y * 0.5, 612) * 0.4);
  if ((col + row) % 2 === 0) c = scale(c, 0.965);
  if (fx < 0.7 || fy < 0.7) c = mix(c, K.graniteJoint, 0.7);
  // Speckle.
  if (hash2(Math.floor(x * 2), Math.floor(y * 2), 613) < 0.08) c = scale(c, 0.9);
  // Guide strip for the blind, leading to the station doors.
  const tx = Math.floor(x / T);
  if ((tx === 86 || tx === 87) && y > 14 * T && y < 22 * T) {
    const gx = x - tx * T;
    if (gx > 6 && gx < 10) c = mix(c, hex('#d8b23a'), 0.8);
  }
  void map;
  return c;
}

/** Fort courtyard: coral-stone slabs with sandy lime joints, bleached by the sun. */
function coral(x: number, y: number): RGB {
  const rowH = 8;
  const row = Math.floor(y / rowH);
  const off = (row % 2) * 6;
  const w = 12;
  const col = Math.floor((x + off) / w);
  const fx = x + off - col * w, fy = y - row * rowH;
  let c = mix(K.coralA, K.coralB, hash2(col, row, 621) * 0.7 + noise2(x * 0.3, y * 0.3, 622) * 0.3);
  // Pits in the coral stone.
  if (hash2(Math.floor(x * 1.5), Math.floor(y * 1.5), 623) < 0.07) c = scale(c, 0.86);
  if (fx < 0.9 || fy < 0.9) c = mix(c, K.coralJoint, 0.55);
  else if (fy < 1.8) c = scale(c, 1.05);
  c = mix(c, hex('#d6c7a6'), fbm(x / 30, y / 30, 624) * 0.3);
  return c;
}

/** Herringbone red brick: 6×3 px bricks alternating direction, worn pale along the walked middle of the lane. */
function herringbone(map: MapDef, x: number, y: number): RGB {
  const u = 3;
  // Herringbone: in each 2u×2u cell, two bricks of 2u×u laid at right angles.
  const cx = Math.floor(x / (2 * u)), cy = Math.floor(y / (2 * u));
  const fx = x - cx * 2 * u, fy = y - cy * 2 * u;
  const diag = (cx + cy) % 2 === 0;
  // Which brick within the cell, and the brick-local coordinates.
  let id: number, jx: number, jy: number;
  if (diag) {
    // horizontal brick on top, vertical brick below-left... approximate with a weave.
    if (fy < u) { id = 0; jx = fx; jy = fy; } else { id = 1; jx = fy - u; jy = fx; }
  } else {
    if (fx < u) { id = 2; jx = fy; jy = fx; } else { id = 3; jx = fx - u; jy = fy; }
  }
  const bx = cx * 4 + id, by = cy;
  const tone = hash2(bx, by, 631);
  let c = tone < 0.07 ? K.brickRed : tone < 0.22 ? K.brickC : tone < 0.7 ? K.brickA : K.brickB;
  c = mix(c, K.brickPale, noise2(x * 0.2, y * 0.2, 632) * 0.25);
  if (hash2(bx, by, 633) < 0.05) c = mix(c, hex('#6e6a66'), 0.7); // a grey replacement brick
  const joint = jx < 0.7 || jy < 0.7 || jx > 2 * u - 0.7;
  if (joint) c = mix(c, K.mortar, 0.6);
  // Wear: paler and smoother towards the middle of the lane.
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const openUp = at(map, tx, ty - 1) === '*', openDown = at(map, tx, ty + 1) === '*';
  const mid = openUp && openDown ? 1 : openUp || openDown ? 0.5 : 0.2;
  c = mix(c, K.brickPale, mid * 0.28 * (0.6 + noise2(x * 0.1, y * 0.1, 634) * 0.4));
  // Grease and shade in the market lane; a puddle gleam here and there.
  if (inRect(tx, ty, MARKET_LANE)) c = mix(scale(c, 0.84 - fbm(x / 20, y / 20, 635) * 0.12), hex('#6e6a66'), 0.25);
  return scale(c, 0.95 + noise2(x * 0.8, y * 0.8, 636) * 0.1);
}

/** The Confucius Temple wall seen from above: vermilion plaster, grey tile coping on the sunny side. */
function redWall(map: MapDef, x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fy = y - ty * T, fx = x - tx * T;
  const below = at(map, tx, ty + 1), above = at(map, tx, ty - 1);
  const left = at(map, tx - 1, ty), right = at(map, tx + 1, ty);
  let c = mix(K.wallRed, K.wallRedL, noise2(x * 0.4, y * 0.4, 641) * 0.5 + fbm(x / 18, y / 18, 642) * 0.3);
  // Ridge tiles run along the wall's length.
  const vertical = (above === '^' && below === '^') || ((above === '^' || below === '^') && left !== '^' && right !== '^');
  if (!vertical && above === '^' && below !== '^') {
    // The south face of the wall: vermilion plaster shaded under the coping, a grey stone base course.
    let f = mix(K.wallRed, K.wallRedL, noise2(x * 0.3, y * 0.3, 645) * 0.4 + fbm(x / 16, y / 16, 646) * 0.2);
    f = scale(f, 1.1 - (fy / T) * 0.28);
    if (fy < 1.6) f = mix(f, K.copingL, 0.7);
    else if (fy < 3.4) f = scale(f, 0.72);
    if (fy > T - 3.2) f = mix(f, hex('#7a6e66'), 0.65);
    if (hash2(Math.floor(x / 2), Math.floor(y / 2), 647) < 0.04) f = scale(f, 0.9);
    return f;
  }
  const k = vertical ? fx : fy;
  const ridge = Math.abs(k - T / 2) < 2.2;
  const tileRow = Math.floor((vertical ? y : x) / 3) % 2;
  if (ridge) c = mix(c, tileRow ? K.coping : K.copingL, 0.9);
  else if (Math.abs(k - T / 2) < 4.5) c = mix(c, hash2(Math.floor((vertical ? y : x) / 3), Math.floor(k / 2), 643) < 0.5 ? K.coping : K.copingL, 0.75);
  // Shadow side (south edge) and a lit top edge.
  if (!vertical && below !== '^' && below !== 'B' && below !== 'K') c = scale(c, 1 - smoothstep(T - 4, T, fy) * 0.25);
  if (!vertical && above !== '^') c = scale(c, 1 + smoothstep(3, 0, fy) * 0.12);
  if (hash2(Math.floor(x / 2), Math.floor(y / 2), 644) < 0.03) c = scale(c, 0.9);
  return c;
}

/** Fort rampart top: Dutch red brick with lime mortar and a weathered white crust, moss in the cracks. */
function rampart(x: number, y: number): RGB {
  const bw = 6, bh = 3;
  const row = Math.floor(y / bh);
  const off = (row % 2) * (bw / 2);
  const col = Math.floor((x + off) / bw);
  const fx = x + off - col * bw, fy = y - row * bh;
  let c = mix(K.dutchA, K.dutchB, hash2(col, row, 651) * 0.8 + noise2(x * 0.5, y * 0.5, 652) * 0.2);
  if (fx < 0.7 || fy < 0.7) c = mix(c, K.lime, 0.6);
  const crust = fbm(x / 14, y / 14, 653);
  if (crust > 0.58) c = mix(c, K.lime, (crust - 0.58) * 2.2);
  if (crust < 0.3 && hash2(Math.floor(x / 2), Math.floor(y / 2), 654) < 0.35) c = mix(c, K.moss, 0.5);
  return c;
}

export function colorAtTainan(map: MapDef, x: number, y: number, mat: string): RGB | null {
  if (mat !== '&' && mat !== '*' && mat !== '^') return null;
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const here = map.id === 'tainan';
  switch (mat) {
    case '&':
      if (here && inRect(tx, ty, HSR_PLAZA)) return granite(map, x, y);
      if (here && inRect(tx, ty, FORT)) return coral(x, y);
      return slabs(x, y, 601);
    case '*':
      return herringbone(map, x, y);
    default:
      if (here && inRect(tx, ty, FORT)) return rampart(x, y);
      return redWall(map, x, y);
  }
}

/** Flat colours shown while the detailed ground is still rendering. */
export const PLACEHOLDER_TAINAN: Record<string, string> = { '&': '#aeaba0', '*': '#b0503a', '^': '#b8382c' };
